import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getButikkKapasiteter,
  getKapasitetsOverstyringer,
} from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  fmtMinutes,
  prettifyStoreName,
  getWeekdayNameNo,
  normalizeEnumDay,
} from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function getWeekOverrideForStore(overrides = [], storeSlug, weekIso, field = "uke") {
  const wanted = normalizeWorkshopSlug(storeSlug);

  return overrides.find((o) => {
    const loc = normalizeWorkshopSlug(o?.lokasjon || "");
    if (loc !== wanted) return false;
    if (!o?.[field]) return false;
    return weekStartISO(o[field]) === weekIso;
  });
}

function getDateOverrideForStore(overrides = [], storeSlug, dateIso) {
  const wanted = normalizeWorkshopSlug(storeSlug);

  return overrides.find((o) => {
    const loc = normalizeWorkshopSlug(o?.lokasjon || "");
    if (loc !== wanted) return false;
    return o?.dato === dateIso;
  });
}

function safeDateIso(value) {
  if (!value) return "";

  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getBookingType(serviceRow = {}, parentItem = {}, parentObj = {}) {
  return (
    serviceRow?.bookingType ||
    serviceRow?.bookingtype ||
    serviceRow?.bookingValg ||
    serviceRow?.bookingvalg ||
    parentItem?.bookingType ||
    parentItem?.bookingtype ||
    parentItem?.bookingValg ||
    parentItem?.bookingvalg ||
    parentObj?.bookingType ||
    parentObj?.bookingtype ||
    parentObj?.bookingValg ||
    parentObj?.bookingvalg ||
    parentObj?.booking?.type ||
    parentObj?.booking?.bookingType ||
    "–"
  );
}

function aggregateModalItems(items = []) {
  const map = new Map();

  for (const item of Array.isArray(items) ? items : []) {
    const key =
      `${item.sourceType || ""}::${item.sourceRecordId || ""}`;

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        minutes: Number(item.minutes || 0),
        serviceNames: [item.itemName].filter(Boolean),
        kategorier: [item.kategori].filter(Boolean),
      });
      continue;
    }

    const existing = map.get(key);

    existing.minutes += Number(item.minutes || 0);

    if (item.itemName && !existing.serviceNames.includes(item.itemName)) {
      existing.serviceNames.push(item.itemName);
    }

    if (item.kategori && !existing.kategorier.includes(item.kategori)) {
      existing.kategorier.push(item.kategori);
    }

    if (!existing.leveringsDato && item.leveringsDato) {
      existing.leveringsDato = item.leveringsDato;
    }

    if (!existing.forventetFerdigDato && item.forventetFerdigDato) {
      existing.forventetFerdigDato = item.forventetFerdigDato;
    }

    if (!existing.dateIso && item.dateIso) {
      existing.dateIso = item.dateIso;
    }

    existing.dateCandidates = uniqueNonEmpty([
      ...(existing.dateCandidates || []),
      ...(item.dateCandidates || []),
    ]);
  }

  return Array.from(map.values()).map((item) => ({
    ...item,
    itemName:
      item.parentItemName ||
      item.serviceNames?.[0] ||
      item.itemName ||
      "Uten navn",
    serviceSummary:
      item.serviceNames && item.serviceNames.length > 1
        ? item.serviceNames.join(", ")
        : "",
    kategori:
      item.kategorier && item.kategorier.length > 1
        ? item.kategorier.join(", ")
        : item.kategori,
  }));
}

function fmtDateNo(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";
}

function normalizeBookingTypeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function isDayBookingType(value) {
  const t = normalizeBookingTypeValue(value);
  return t === "dag" || t === "drop-in" || t === "dropin";
}




function shouldShowInWeekModal(item) {
  const t = item?.bookingType;
  return !isDayBookingType(t);
}

function shouldShowInDayModal(item, dateIso) {
  if (!item) return false;

  if (item.dateIso === dateIso) return true;

  if (Array.isArray(item.dateCandidates) && item.dateCandidates.includes(dateIso)) {
    return true;
  }

  return false;
}

function uniqueNonEmpty(values = []) {
  return [...new Set(values.filter(Boolean))];
}

function getBestLeveringsDato(serviceRow = {}, parentItem = {}, parentObj = {}) {
  return (
    serviceRow?.leveringsDato ||
    parentItem?.leveringsDato ||
    parentObj?.leveringsDato ||
    null
  );
}

function getBestFerdigDato(serviceRow = {}, parentItem = {}, parentObj = {}) {
  return (
    serviceRow?.ferdigDato ||
    serviceRow?.forventetFerdigDato ||
    parentItem?.ferdigDato ||
    parentItem?.forventetFerdigDato ||
    parentObj?.ferdigDato ||
    parentObj?.forventetFerdigDato ||
    null
  );
}

function collectDateCandidates(serviceRow = {}, parentItem = {}, parentObj = {}) {
  return uniqueNonEmpty([
    safeDateIso(serviceRow?.ferdigDato),
    safeDateIso(serviceRow?.forventetFerdigDato),
    safeDateIso(parentItem?.ferdigDato),
    safeDateIso(parentItem?.forventetFerdigDato),
    safeDateIso(parentObj?.ferdigDato),
    safeDateIso(parentObj?.forventetFerdigDato),

    safeDateIso(serviceRow?.leveringsDato),
    safeDateIso(parentItem?.leveringsDato),
    safeDateIso(parentObj?.leveringsDato),

    safeDateIso(serviceRow?.bookingDato),
    safeDateIso(parentItem?.bookingDato),
    safeDateIso(parentObj?.bookingDato),

    safeDateIso(serviceRow?.dato),
    safeDateIso(parentItem?.dato),
    safeDateIso(parentObj?.dato),

    safeDateIso(parentObj?.createdAt),
  ]);
}

function getServiceRows(item = {}) {
  return [
    ...(Array.isArray(item?.items) ? item.items : []),
    ...(Array.isArray(item?.tjenester) ? item.tjenester : []),
    ...(Array.isArray(item?.serviceLines) ? item.serviceLines : []),
  ];
}

function getWorkshopServiceRows(svc = {}, product = {}) {
  return [
    ...(Array.isArray(svc?.serviceLines) ? svc.serviceLines : []),
    ...(Array.isArray(product?.serviceLines) ? product.serviceLines : []),
    ...(Array.isArray(product?.items) ? product.items : []),
    ...(Array.isArray(product?.tjenester) ? product.tjenester : []),
  ];
}

function sumServiceRowsMinutes(serviceRows = []) {
  return (Array.isArray(serviceRows) ? serviceRows : []).reduce((sum, row) => {
    return (
      sum +
      (Number(row?.tidsbrukMinutter) ||
        Number(row?.estimatedMinutes) ||
        Number(row?.minutter) ||
        Number(row?.timeEstimateMinutes) ||
        Number(row?.tidsbruk) ||
        Number(row?.durationMinutes) ||
        Number(row?.tidsestimat) ||
        0)
    );
  }, 0);
}

function getBookingMinutes(item = {}, booking = {}, serviceRow = null) {
  const serviceRows = getServiceRows(item);
  const sumRows = sumServiceRowsMinutes(serviceRows);

  const directCandidates = [
    serviceRow?.tidsbrukMinutter,
    serviceRow?.estimatedMinutes,
    serviceRow?.minutter,
    serviceRow?.timeEstimateMinutes,
    serviceRow?.tidsbruk,
    serviceRow?.durationMinutes,

    item?.tidsbrukMinutter,
    item?.estimatedMinutes,
    item?.minutter,
    item?.timeEstimateMinutes,
    item?.tidsbruk,
    item?.durationMinutes,
    item?.totalTidsbrukMinutter,

    booking?.tidsbrukMinutter,
    booking?.estimatedMinutes,
    booking?.minutter,
    booking?.timeEstimateMinutes,
    booking?.tidsbruk,
    booking?.totalTidsbrukMinutter,
    booking?.totalMinutes,
  ];

  for (const candidate of directCandidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) return n;
  }

  return sumRows || 0;
}

function getWorkshopMinutesFromService(svc = {}, product = {}, serviceRow = null) {
  const serviceRows = getWorkshopServiceRows(svc, product);
  const sumRows = sumServiceRowsMinutes(serviceRows);

  const directCandidates = [
    serviceRow?.tidsbrukMinutter,
    serviceRow?.estimatedMinutes,
    serviceRow?.minutter,
    serviceRow?.timeEstimateMinutes,
    serviceRow?.tidsbruk,
    serviceRow?.durationMinutes,

    svc?.tidsbrukMinutter,
    svc?.estimatedMinutes,
    svc?.minutter,
    svc?.timeEstimateMinutes,
    svc?.tidsbruk,
    svc?.durationMinutes,
    svc?.totalTidsbrukMinutter,
    svc?.totalMinutes,
    svc?.totalTid,
    svc?.arbeidstidMinutter,
    svc?.estimatMinutter,

    product?.tidsbrukMinutter,
    product?.estimatedMinutes,
    product?.minutter,
    product?.timeEstimateMinutes,
    product?.tidsbruk,
    product?.durationMinutes,
    product?.tidsestimat,
    product?.totalTidsbrukMinutter,
  ];

  for (const candidate of directCandidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) return n;
  }

  if (sumRows > 0) return sumRows;

  return 0;
}

function guessItemMinutes(item = {}) {
  return (
    Number(item?.tidsbrukMinutter) ||
    Number(item?.estimatedMinutes) ||
    Number(item?.minutter) ||
    Number(item?.timeEstimateMinutes) ||
    Number(item?.tidsbruk) ||
    Number(item?.totalTidsbrukMinutter) ||
    0
  );
}

function getProductDisplayName(item = {}) {
  return (
    item?.produktnavn ||
    item?.productName ||
    item?.navn ||
    item?.title ||
    item?.produkt?.navn ||
    item?.produkt?.title ||
    item?.servicenavn ||
    item?.slug ||
    item?.produktmerke ||
    item?.produkttype ||
    item?.kategori ||
    "Uten navn"
  );
}

function getCustomerName(customer = {}, fallbackObj = {}) {
  const direct =
    [customer?.firstName, customer?.lastName]
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .join(" ") ||
    String(customer?.name || "").trim() ||
    String(customer?.fullName || "").trim() ||
    "";

  if (direct) return direct;

  return (
    [fallbackObj?.firstName, fallbackObj?.lastName]
      .map((v) => String(v || "").trim())
      .filter(Boolean)
      .join(" ") ||
    String(fallbackObj?.customerName || "").trim() ||
    String(fallbackObj?.kundeNavn || "").trim() ||
    String(fallbackObj?.navn || "").trim() ||
    String(fallbackObj?.fullName || "").trim() ||
    String(fallbackObj?.name || "").trim() ||
    ""
  );
}

function getPhone(customer = {}, fallbackObj = {}) {
  return (
    String(customer?.phoneNumber || "").trim() ||
    String(customer?.phone || "").trim() ||
    String(customer?.mobil || "").trim() ||
    String(fallbackObj?.phoneNumber || "").trim() ||
    String(fallbackObj?.phone || "").trim() ||
    String(fallbackObj?.mobil || "").trim() ||
    String(fallbackObj?.telefon || "").trim() ||
    ""
  );
}

function getWorkshopCustomerFallback(svc = {}, product = {}) {
  return (
    svc?.customerSnapshot ||
    svc?.originalCustomer ||
    svc?.kunde ||
    svc?.customer ||
    svc?.customerInfo ||
    svc?.booking?.customer ||
    svc?.booking?.kunde ||
    product?.customerSnapshot ||
    product?.customer ||
    product?.kunde ||
    product?.originalCustomer ||
    product?.customerInfo ||
    {}
  );
}

function isSykkelserviceItem(item = {}) {
  const produktKategori = String(
    item?.kategori ||
      item?.produkttype ||
      item?.produktkategori ||
      item?.servicekategori ||
      ""
  )
    .trim()
    .toLowerCase();

  if (produktKategori === "sykkelservice") return true;

  const serviceRows = getServiceRows(item);

  return serviceRows.some((row) => {
    const rowKategori = String(
      row?.kategori ||
        row?.produkttype ||
        row?.produktkategori ||
        row?.servicekategori ||
        row?.servicetype ||
        row?.slug ||
        ""
    )
      .trim()
      .toLowerCase();

    return rowKategori.includes("sykkelservice");
  });
}

function applyWeekCapacity(baseWeekMin, override) {
  if (!override) return Math.max(0, Number(baseWeekMin) || 0);
  if (override?.stengt) return 0;

  let val = Number(baseWeekMin) || 0;
  if (typeof override?.minutter === "number") {
    val += override.minutter;
  }

  return Math.max(0, val);
}

function applyDayCapacity(baseDayMin, weekOverride, dateOverride) {
  if (dateOverride?.stengt) return 0;

  let val = Number(baseDayMin) || 0;

  if (typeof dateOverride?.minutter === "number") {
    val += dateOverride.minutter;
    return Math.max(0, val);
  }

  if (weekOverride?.stengt) return 0;

  if (typeof weekOverride?.minutter === "number") {
    val += Math.floor(Number(weekOverride.minutter || 0) / 5);
  }

  return Math.max(0, val);
}

// Bygger en oversiktsrad for et 3-dagers delvindu av uken (tidliguke:
// man–ons, senuke: ons–fre) — samme prinsipp som ukeraden, men med eget
// budsjett, eget override-felt og et 3-dagers vindu i stedet for 5.
function buildSubWeekOverviewRow({
  monday,
  weekIso,
  storeSlug,
  overrides,
  baseBudgetMin,
  usedMin,
  overrideField,
  startOffset,
  endOffset,
  weekNo,
  year,
}) {
  const windowDays = endOffset - startOffset + 1;

  const startDate = new Date(monday);
  startDate.setDate(monday.getDate() + startOffset);
  const startIso = isoDate(startDate);
  const endDate = new Date(monday);
  endDate.setDate(monday.getDate() + endOffset);
  const endIso = isoDate(endDate);

  const subOverride = getWeekOverrideForStore(overrides, storeSlug, weekIso, overrideField);
  let kapasitetMin = applyWeekCapacity(baseBudgetMin, subOverride);

  const closedDayISOs = [];
  if (!subOverride?.stengt) {
    for (let dayI = startOffset; dayI <= endOffset; dayI++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + dayI);
      const dayIso = isoDate(dayDate);
      const datoOv = getDateOverrideForStore(overrides, storeSlug, dayIso);
      if (datoOv?.stengt) closedDayISOs.push(dayIso);
    }
    if (closedDayISOs.length > 0) {
      kapasitetMin = Math.floor(kapasitetMin * (windowDays - closedDayISOs.length) / windowDays);
    }
  }

  const isStartClosed = closedDayISOs.includes(startIso);
  const isEndClosed = closedDayISOs.includes(endIso);

  let intakeDate = startIso;
  let pickupDate = endIso;
  if (isStartClosed) {
    const nextDay = new Date(startDate);
    nextDay.setDate(startDate.getDate() + 1);
    intakeDate = isoDate(nextDay);
  }
  if (isEndClosed) {
    const prevDay = new Date(endDate);
    prevDay.setDate(endDate.getDate() - 1);
    pickupDate = isoDate(prevDay);
  }

  const bruktMin = Number(usedMin || 0);
  const ledigMin = Math.max(0, kapasitetMin - bruktMin);

  return {
    weekIso,
    weekNo,
    year,
    kapasitetMin,
    bruktMin,
    ledigMin,
    fyllingsgrad: kapasitetMin > 0 ? Math.round((bruktMin / kapasitetMin) * 100) : 0,
    harOverride: !!subOverride,
    stengt: !!subOverride?.stengt,
    closedDayISOs,
    isStartClosed,
    isEndClosed,
    intakeDate,
    pickupDate,
  };
}

function buildBookingDetails(bookings = [], wantedStore) {
  const rows = [];

  for (const booking of Array.isArray(bookings) ? bookings : []) {
    if (booking?.convertedToServicesAt) continue;
    if (String(booking?.status || "").toLowerCase() !== "booking") continue;

    const bookingId = booking?._id || booking?.id;
    const items = Array.isArray(booking?.items) ? booking.items : [];

    for (const item of items) {
      const itemStore = normalizeWorkshopSlug(
        item?.verkstedSlug || item?.butikkSlug || item?.butikk || ""
      );

      if (itemStore !== wantedStore) continue;

      const serviceRows = getServiceRows(item);

      if (!serviceRows.length) {
        const dateCandidates = collectDateCandidates({}, item, booking);
        const dateIso = dateCandidates[0] || "";
        const leveringsDato = getBestLeveringsDato({}, item, booking);
        const forventetFerdigDato = getBestFerdigDato({}, item, booking);

rows.push({
  id: `${bookingId || "booking"}-${item?.produktId || item?._id || Math.random()}`,
  sourceType: "booking",
  sourceRecordId: bookingId,
  status: booking?.status || "booking",
  bookingType: getBookingType({}, item, booking),
  customerName: getCustomerName(booking?.customer || {}, booking),
  phone: getPhone(booking?.customer || {}, booking),
  itemName: getProductDisplayName(item),
  parentItemName: "",
  kategori:
    item?.kategori ||
    item?.produkttype ||
    item?.produktkategori ||
    item?.servicekategori ||
    "",
  brand: item?.produktmerke || "",
  color: item?.farge || "",
  store:
    item?.butikk ||
    item?.verkstedSlug ||
    item?.butikkSlug ||
    prettifyStoreName(wantedStore),
  minutes: getBookingMinutes(item, booking, null) || guessItemMinutes(item),
  dateIso,
  dateCandidates,
  weekIso: dateIso ? weekStartISO(dateIso) : "",
  leveringsDato,
  forventetFerdigDato,
  raw: item,
});

        continue;
      }

      for (const serviceRow of serviceRows) {
        const dateCandidates = collectDateCandidates(serviceRow, item, booking);
        const dateIso = dateCandidates[0] || "";
        const leveringsDato = getBestLeveringsDato(serviceRow, item, booking);
        const forventetFerdigDato = getBestFerdigDato(serviceRow, item, booking);

rows.push({
  id: `${bookingId || "booking"}-${item?.produktId || item?._id || "item"}-${serviceRow?._id || serviceRow?.id || Math.random()}`,
  sourceType: "booking",
  sourceRecordId: bookingId,
  status: booking?.status || "booking",
  bookingType: getBookingType(serviceRow, item, booking),
  customerName: getCustomerName(booking?.customer || {}, booking),
  phone: getPhone(booking?.customer || {}, booking),
  itemName:
    serviceRow?.navn ||
    serviceRow?.title ||
    serviceRow?.servicenavn ||
    serviceRow?.slug ||
    getProductDisplayName(item),
  parentItemName: getProductDisplayName(item),
  kategori:
    serviceRow?.kategori ||
    serviceRow?.produkttype ||
    serviceRow?.produktkategori ||
    serviceRow?.servicekategori ||
    item?.kategori ||
    item?.produkttype ||
    "",
  brand: item?.produktmerke || "",
  color: item?.farge || "",
  store:
    item?.butikk ||
    item?.verkstedSlug ||
    item?.butikkSlug ||
    prettifyStoreName(wantedStore),
  minutes: getBookingMinutes(item, booking, serviceRow),
  dateIso,
  dateCandidates,
  weekIso: dateIso ? weekStartISO(dateIso) : "",
  leveringsDato,
  forventetFerdigDato,
  raw: serviceRow,
});
      }
    }
  }

  return rows;
}

function buildWorkshopDetails(services = [], wantedStore) {
  const rows = [];

  for (const svc of Array.isArray(services) ? services : []) {
    const svcStatus = String(svc?.status || "").toLowerCase();
    if (svcStatus === "avsluttet" || svcStatus === "kansellert") continue;

    const workshopId = svc?._id || svc?.id;
    const product = svc?.produkt || {};
    const customerFallback = getWorkshopCustomerFallback(svc, product);

    const itemStore = normalizeWorkshopSlug(
      product?.verkstedSlug || product?.butikkSlug || product?.butikk || ""
    );

    if (itemStore !== wantedStore) continue;

    const serviceRows = getWorkshopServiceRows(svc, product);

    if (!serviceRows.length) {
      const dateCandidates = collectDateCandidates({}, product, svc);
      const dateIso = dateCandidates[0] || "";
      const leveringsDato = getBestLeveringsDato({}, product, svc);
      const forventetFerdigDato = getBestFerdigDato({}, product, svc);

rows.push({
  id: `${workshopId || "workshop"}`,
  sourceType: "workshop",
  sourceRecordId: workshopId,
  status: svc?.status || "",
  bookingType: getBookingType({}, product, svc),
  customerName: getCustomerName(svc?.customer || {}, customerFallback),
  phone: getPhone(svc?.customer || {}, customerFallback),
  itemName: getProductDisplayName(product),
  parentItemName: "",
  kategori:
    product?.kategori ||
    product?.produkttype ||
    product?.produktkategori ||
    product?.servicekategori ||
    "",
  brand: product?.produktmerke || "",
  color: product?.farge || "",
  store:
    product?.butikk ||
    product?.verkstedSlug ||
    product?.butikkSlug ||
    prettifyStoreName(wantedStore),
  minutes: getWorkshopMinutesFromService(svc, product, null),
  dateIso,
  dateCandidates,
  weekIso: dateIso ? weekStartISO(dateIso) : "",
  leveringsDato,
  forventetFerdigDato,
  raw: svc,
});

      continue;
    }

    for (const serviceRow of serviceRows) {
      const dateCandidates = collectDateCandidates(serviceRow, product, svc);
      const dateIso = dateCandidates[0] || "";
      const leveringsDato = getBestLeveringsDato(serviceRow, product, svc);
      const forventetFerdigDato = getBestFerdigDato(serviceRow, product, svc);

rows.push({
  id: `${workshopId || "workshop"}-${serviceRow?._id || serviceRow?.id || Math.random()}`,
  sourceType: "workshop",
  sourceRecordId: workshopId,
  status: svc?.status || "",
  bookingType: getBookingType(serviceRow, product, svc),
  customerName: getCustomerName(svc?.customer || {}, customerFallback),
  phone: getPhone(svc?.customer || {}, customerFallback),
  itemName:
    serviceRow?.navn ||
    serviceRow?.title ||
    serviceRow?.servicenavn ||
    serviceRow?.slug ||
    getProductDisplayName(product),
  parentItemName: getProductDisplayName(product),
  kategori:
    serviceRow?.kategori ||
    serviceRow?.produkttype ||
    serviceRow?.produktkategori ||
    serviceRow?.servicekategori ||
    product?.kategori ||
    product?.produkttype ||
    "",
  brand: product?.produktmerke || "",
  color: product?.farge || "",
  store:
    product?.butikk ||
    product?.verkstedSlug ||
    product?.butikkSlug ||
    prettifyStoreName(wantedStore),
  minutes: getWorkshopMinutesFromService(svc, product, serviceRow),
  dateIso,
  dateCandidates,
  weekIso: dateIso ? weekStartISO(dateIso) : "",
  leveringsDato,
  forventetFerdigDato,
  raw: serviceRow,
});
    }
  }

  return rows;
}

function getDetailPath(item) {
  if (!item?.sourceRecordId) return null;

  if (item.sourceType === "booking") {
    return `/booking/${item.sourceRecordId}`;
  }

  if (item.sourceType === "workshop") {
    return `/workshop/${item.sourceRecordId}`;
  }

  return null;
}

function CapacityDetailsModal({ open, onClose, title, items = [], onOpenDetail }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-7xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <div className="text-sm text-gray-500">{items.length} rad(er)</div>
          </div>
          <button
            onClick={onClose}
            className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100"
          >
            Lukk
          </button>
        </div>

        <div className="p-4 overflow-auto max-h-[75vh]">
          {items.length ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Bookingtype</th>
                  <th className="px-3 py-2 text-left">Kunde</th>
                  <th className="px-3 py-2 text-left">Tjeneste / artikkel</th>
                  <th className="px-3 py-2 text-left">Kategori</th>
                  <th className="px-3 py-2 text-left">Merke</th>
                  <th className="px-3 py-2 text-left">Leveringsdato</th>
                  <th className="px-3 py-2 text-left">Forventet ferdig</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Minutter</th>
                  <th className="px-3 py-2 text-right">Detaljer</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const detailPath = getDetailPath(item);

                  return (
                    <tr key={item.id} className="border-t align-top">
<td className="px-3 py-2">
  {item.bookingType || "–"}
</td>

                      <td className="px-3 py-2">
                        <div className="font-medium">{item.customerName || "–"}</div>
                        {item.phone ? (
                          <div className="text-xs text-gray-500">{item.phone}</div>
                        ) : null}
                      </td>

<td className="px-3 py-2">
  <div className="font-medium">{item.itemName}</div>

  {item.serviceSummary ? (
    <div className="text-xs text-gray-500">{item.serviceSummary}</div>
  ) : item.parentItemName ? (
    <div className="text-xs text-gray-500">{item.parentItemName}</div>
  ) : null}

  {item.color ? (
    <div className="text-xs text-gray-500">{item.color}</div>
  ) : null}
</td>

                      <td className="px-3 py-2">{item.kategori || "–"}</td>
                      <td className="px-3 py-2">{item.brand || "–"}</td>
                      <td className="px-3 py-2">
                        {item.leveringsDato ? fmtDateNo(item.leveringsDato) : "–"}
                      </td>
                      <td className="px-3 py-2">
                        {item.forventetFerdigDato
                          ? fmtDateNo(item.forventetFerdigDato)
                          : "–"}
                      </td>
                      <td className="px-3 py-2">{item.status || "–"}</td>
                      <td className="px-3 py-2 text-right">
                        {fmtMinutes(item.minutes || 0)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {detailPath ? (
                          <button
                            type="button"
                            onClick={() => onOpenDetail(item)}
                            className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50"
                          >
                            Åpne
                          </button>
                        ) : (
                          <span className="text-gray-400">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="py-10 text-center text-gray-500">
              Ingen artikler eller sykler funnet for dette valget.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Samme tabellprinsipp som Ukeoversikt, men for et 3-dagers delvindu
// (tidliguke: man–ons, senuke: ons–fre).
function SubWeekOverviewSection({ title, storeSlug, rows, totals, currentWeekIso, onRowClick }) {
  return (
    <>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="rounded border p-4 bg-blue-50">
          <div className="text-sm text-gray-600">Total {title.toLowerCase()}kapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.kapasitet)}</div>
        </div>
        <div className="rounded border p-4 bg-amber-50">
          <div className="text-sm text-gray-600">Brukt {title.toLowerCase()}kapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.brukt)}</div>
        </div>
        <div className="rounded border p-4 bg-green-50">
          <div className="text-sm text-gray-600">Ledig {title.toLowerCase()}kapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.ledig)}</div>
        </div>
      </div>

      <div className="rounded border bg-white mb-8 overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50 font-semibold">
          {title}oversikt – {prettifyStoreName(storeSlug)}
        </div>

        <table className="min-w-full">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Verksted</th>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-left">Uke start</th>
              <th className="px-4 py-2 text-left">Innlevering</th>
              <th className="px-4 py-2 text-left">Henting</th>
              <th className="px-4 py-2 text-right">Kapasitet</th>
              <th className="px-4 py-2 text-right">Brukt</th>
              <th className="px-4 py-2 text-right">Ledig</th>
              <th className="px-4 py-2 text-right">Fyllingsgrad</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.weekIso}
                className="border-t cursor-pointer hover:bg-blue-50"
                onClick={() => onRowClick(row)}
                title="Klikk for å se artikler/sykler i dette vinduet"
              >
                <td className="px-4 py-2">{prettifyStoreName(storeSlug)}</td>
                <td className="px-4 py-2 font-medium">
                  Uke {row.weekNo} / {row.year}
                </td>
                <td className="px-4 py-2">{row.weekIso}</td>
                <td className="px-4 py-2">
                  {row.isStartClosed ? (
                    <span className="text-amber-700 font-medium">{row.intakeDate} ⚠️</span>
                  ) : (
                    row.intakeDate
                  )}
                </td>
                <td className="px-4 py-2">
                  {row.isEndClosed ? (
                    <span className="text-amber-700 font-medium">{row.pickupDate} ⚠️</span>
                  ) : (
                    row.pickupDate
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {fmtMinutes(row.kapasitetMin)}
                  {row.harOverride && (
                    <span className="ml-2 text-xs text-blue-600">
                      {row.stengt ? "(stengt)" : "(override)"}
                    </span>
                  )}
                  {row.closedDayISOs?.length > 0 && (
                    <span className="ml-2 text-xs text-amber-600">
                      ({row.closedDayISOs.length} dag(er) stengt)
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktMin)}</td>
                <td className="px-4 py-2 text-right">
                  {row.weekIso === currentWeekIso ? "–" : fmtMinutes(row.ledigMin)}
                </td>
                <td className="px-4 py-2 text-right">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      row.fyllingsgrad >= 90
                        ? "bg-red-100 text-red-700"
                        : row.fyllingsgrad >= 70
                        ? "bg-amber-100 text-amber-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {row.fyllingsgrad} %
                  </span>
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                  Ingen data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function WorkshopCapacityOverviewTab({
  storeSlug,
  weeksAhead,
}) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [capacityRows, setCapacityRows] = useState([]);
  const [tidligUkeRows, setTidligUkeRows] = useState([]);
  const [senUkeRows, setSenUkeRows] = useState([]);
  const [dayRows, setDayRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [fortlopende, setFortlopende] = useState({
    count: 0,
    min: 0,
  });
  const [bikeStats, setBikeStats] = useState({
    fysiskInne: 0,
    ikkeLevert: 0,
  });

  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    items: [],
  });

  useEffect(() => {
    const run = async () => {
      setLoading(true);

      try {
        const today = new Date();
        const fromISO = isoDate(today);

        const toDate = new Date(today);
        toDate.setDate(toDate.getDate() + weeksAhead * 7);
        const toISO = isoDate(toDate);

        const url = `${API_BASE_URL}/capacity?verkstedSlug=${encodeURIComponent(
          storeSlug
        )}&from=${fromISO}&to=${toISO}`;

        const [capacityRes, butikker, overrides, bookingsRes, workshopRes] =
          await Promise.all([
            fetch(url).then((r) => r.json()),
            getButikkKapasiteter(),
            getKapasitetsOverstyringer(),
            fetch(`${API_BASE_URL}/bookings`).then((r) => r.json()),
            fetch(`${API_BASE_URL}/workshop-services`).then((r) => r.json()),
          ]);

          const perUkeMin = capacityRes?.perUkeMin || {};
const perTidligUkeMin = capacityRes?.perTidligUkeMin || {};
const perSenUkeMin = capacityRes?.perSenUkeMin || {};
const perDagMin = capacityRes?.perDagMin || {};
const dropinMin = capacityRes?.dropinMin || {};
const fortlopendeCount = capacityRes?.fortlopendeCount || 0;
const fortlopendeMin = capacityRes?.fortlopendeMin || 0;

const butikk = (butikker || []).find(
  (b) =>
    normalizeWorkshopSlug(b?.slug || "") ===
    normalizeWorkshopSlug(storeSlug)
);

const baseUkekapasitetMin = Number(butikk?.ukebudsjett) || 0;
const baseTidligUkeMin = Number(butikk?.tidligukebudsjett) || 0;
const baseSenUkeMin = Number(butikk?.senukebudsjett) || 0;

let baseDagskapasitetMin = Number(butikk?.dagsbudsjett) || 0;
if (!baseDagskapasitetMin && baseUkekapasitetMin) {
  baseDagskapasitetMin = Math.floor(baseUkekapasitetMin / 5);
}

const allowedDays = (butikk?.dagsbudsjettdager || []).map(normalizeEnumDay);

const weekRows = [];
const tidligUkeRowsAcc = [];
const senUkeRowsAcc = [];
const dailyRows = [];
const wantedStore = normalizeWorkshopSlug(storeSlug);

const bookingDetails = buildBookingDetails(bookingsRes, wantedStore);
const workshopDetails = buildWorkshopDetails(workshopRes, wantedStore);
const combinedDetails = [...bookingDetails, ...workshopDetails];

setDetailRows(combinedDetails);

const fysiskInne = (Array.isArray(workshopRes) ? workshopRes : []).filter((svc) => {
  const svcStatus = String(svc?.status || "").toLowerCase();
  if (svcStatus === "avsluttet" || svcStatus === "kansellert") return false;

  const svcStore = normalizeWorkshopSlug(
    svc?.produkt?.verkstedSlug ||
      svc?.produkt?.butikkSlug ||
      svc?.produkt?.butikk ||
      ""
  );

  if (svcStore !== wantedStore) return false;

  return isSykkelserviceItem(svc?.produkt);
}).length;

const ikkeLevert = (Array.isArray(bookingsRes) ? bookingsRes : []).reduce(
  (sum, booking) => {
    if (booking?.convertedToServicesAt) return sum;
    if (String(booking?.status || "").toLowerCase() !== "booking") return sum;

    const items = Array.isArray(booking?.items) ? booking.items : [];

    const count = items.filter((item) => {
      const itemStore = normalizeWorkshopSlug(
        item?.verkstedSlug || item?.butikkSlug || item?.butikk || ""
      );

      if (itemStore !== wantedStore) return false;

      return isSykkelserviceItem(item);
    }).length;

    return sum + count;
  },
  0
);

setBikeStats({
  fysiskInne,
  ikkeLevert,
});

for (let i = 0; i < weeksAhead; i++) {
  const start = new Date(today);
  const monday = new Date(weekStartISO(start));
  monday.setDate(monday.getDate() + i * 7);

  const weekIso = isoDate(monday);
  const fridayDate = new Date(monday);
  fridayDate.setDate(monday.getDate() + 4);
  const fridayIso = isoDate(fridayDate);

  const ukeOverride = getWeekOverrideForStore(
    overrides,
    storeSlug,
    weekIso
  );

  let ukekapasitetMin = applyWeekCapacity(
    baseUkekapasitetMin,
    ukeOverride
  );

  const closedDayISOs = [];
  if (!ukeOverride?.stengt) {
    for (let dayI = 0; dayI < 5; dayI++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + dayI);
      const dayIso = isoDate(dayDate);
      const datoOv = getDateOverrideForStore(overrides, storeSlug, dayIso);
      if (datoOv?.stengt) closedDayISOs.push(dayIso);
    }
    if (closedDayISOs.length > 0) {
      ukekapasitetMin = Math.floor(ukekapasitetMin * (5 - closedDayISOs.length) / 5);
    }
  }

  const isMondayClosed = closedDayISOs.includes(weekIso);
  const isFridayClosed = closedDayISOs.includes(fridayIso);

  let intakeDate = weekIso;
  let pickupDate = fridayIso;
  if (isMondayClosed) {
    const tuesday = new Date(monday);
    tuesday.setDate(monday.getDate() + 1);
    intakeDate = isoDate(tuesday);
  }
  if (isFridayClosed) {
    const thursday = new Date(fridayDate);
    thursday.setDate(fridayDate.getDate() - 1);
    pickupDate = isoDate(thursday);
  }

  const bruktMin = Number(perUkeMin[weekIso] || 0);
  const ledigMin = Math.max(0, ukekapasitetMin - bruktMin);

  weekRows.push({
    weekIso,
    weekNo: getWeekNumber(monday),
    year: monday.getFullYear(),
    ukekapasitetMin,
    bruktMin,
    ledigMin,
    fyllingsgrad:
      ukekapasitetMin > 0
        ? Math.round((bruktMin / ukekapasitetMin) * 100)
        : 0,
    harOverride: !!ukeOverride,
    stengt: !!ukeOverride?.stengt,
    closedDayISOs,
    isMondayClosed,
    isFridayClosed,
    intakeDate,
    pickupDate,
  });

  tidligUkeRowsAcc.push(
    buildSubWeekOverviewRow({
      monday,
      weekIso,
      storeSlug,
      overrides,
      baseBudgetMin: baseTidligUkeMin,
      usedMin: perTidligUkeMin[weekIso],
      overrideField: "tidliguke",
      startOffset: 0,
      endOffset: 2,
      weekNo: getWeekNumber(monday),
      year: monday.getFullYear(),
    })
  );

  senUkeRowsAcc.push(
    buildSubWeekOverviewRow({
      monday,
      weekIso,
      storeSlug,
      overrides,
      baseBudgetMin: baseSenUkeMin,
      usedMin: perSenUkeMin[weekIso],
      overrideField: "senuke",
      startOffset: 2,
      endOffset: 4,
      weekNo: getWeekNumber(monday),
      year: monday.getFullYear(),
    })
  );
}

for (let i = 0; i < weeksAhead * 7; i++) {
  const d = new Date(today);
  d.setDate(d.getDate() + i);

  const dateIso = isoDate(d);
  const weekIso = weekStartISO(d);

  const weekdayEnum = normalizeEnumDay(
    ["sondag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lordag"][
      d.getDay()
    ]
  );

  if (allowedDays.length && !allowedDays.includes(weekdayEnum)) {
    continue;
  }

  const ukeOverride = getWeekOverrideForStore(overrides, storeSlug, weekIso);
  const datoOverride = getDateOverrideForStore(overrides, storeSlug, dateIso);

  const dagskapasitetMin = applyDayCapacity(
    baseDagskapasitetMin,
    ukeOverride,
    datoOverride
  );

  const bruktDagMin = Number(perDagMin[dateIso] || 0);
  const bruktDropinMin = Number(dropinMin[dateIso] || 0);
  const bruktTotaltMin = bruktDagMin + bruktDropinMin;
  const ledigMin = Math.max(0, dagskapasitetMin - bruktTotaltMin);

  dailyRows.push({
    dateIso,
    weekIso,
    weekNo: getWeekNumber(d),
    weekday: getWeekdayNameNo(d),
    dagskapasitetMin,
    bruktDagMin,
    bruktDropinMin,
    bruktTotaltMin,
    ledigMin,
    fyllingsgrad:
      dagskapasitetMin > 0
        ? Math.round((bruktTotaltMin / dagskapasitetMin) * 100)
        : 0,
    harOverride: !!ukeOverride || !!datoOverride,
    stengt: !!datoOverride?.stengt || !!ukeOverride?.stengt,
  });
}

setCapacityRows(weekRows);
setTidligUkeRows(tidligUkeRowsAcc);
setSenUkeRows(senUkeRowsAcc);
setDayRows(dailyRows);
setFortlopende({
  count: fortlopendeCount,
  min: fortlopendeMin,
});

      } catch (err) {
        console.error("Feil ved henting av kapasitet:", err);
        setCapacityRows([]);
        setTidligUkeRows([]);
        setSenUkeRows([]);
        setDayRows([]);
        setDetailRows([]);
        setFortlopende({
          count: 0,
          min: 0,
        });
        setBikeStats({
          fysiskInne: 0,
          ikkeLevert: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [storeSlug, weeksAhead]);

  const totals = useMemo(() => {
    return capacityRows.reduce(
      (acc, row) => {
        acc.kapasitet += row.ukekapasitetMin;
        acc.brukt += row.bruktMin;
        acc.ledig += row.ledigMin;
        return acc;
      },
      { kapasitet: 0, brukt: 0, ledig: 0 }
    );
  }, [capacityRows]);

  const tidligUkeTotals = useMemo(() => {
    return tidligUkeRows.reduce(
      (acc, row) => {
        acc.kapasitet += row.kapasitetMin;
        acc.brukt += row.bruktMin;
        acc.ledig += row.ledigMin;
        return acc;
      },
      { kapasitet: 0, brukt: 0, ledig: 0 }
    );
  }, [tidligUkeRows]);

  const senUkeTotals = useMemo(() => {
    return senUkeRows.reduce(
      (acc, row) => {
        acc.kapasitet += row.kapasitetMin;
        acc.brukt += row.bruktMin;
        acc.ledig += row.ledigMin;
        return acc;
      },
      { kapasitet: 0, brukt: 0, ledig: 0 }
    );
  }, [senUkeRows]);

  const dayTotals = useMemo(() => {
    return dayRows.reduce(
      (acc, row) => {
        acc.kapasitet += row.dagskapasitetMin;
        acc.bruktDag += row.bruktDagMin;
        acc.bruktDropin += row.bruktDropinMin;
        acc.bruktTotalt += row.bruktTotaltMin;
        acc.ledig += row.ledigMin;
        return acc;
      },
      {
        kapasitet: 0,
        bruktDag: 0,
        bruktDropin: 0,
        bruktTotalt: 0,
        ledig: 0,
      }
    );
  }, [dayRows]);

function openWeekModal(row) {
  const filtered = detailRows.filter(
    (x) => x.weekIso === row.weekIso && shouldShowInWeekModal(x)
  );

  const items = aggregateModalItems(filtered);

  setModalState({
    open: true,
    title: `${prettifyStoreName(storeSlug)} – uke ${row.weekNo} / ${row.year}`,
    items,
  });
}

function openDayModal(row) {
  const filtered = detailRows.filter((x) => shouldShowInDayModal(x, row.dateIso));
  const items = aggregateModalItems(filtered);

  setModalState({
    open: true,
    title: `${prettifyStoreName(storeSlug)} – ${row.dateIso} (${row.weekday})`,
    items,
  });
}

  function closeModal() {
    setModalState({
      open: false,
      title: "",
      items: [],
    });
  }

  function openDetail(item) {
    const path = getDetailPath(item);
    if (!path) return;

    closeModal();
    navigate(path);
  }

  const currentWeekIso = weekStartISO(new Date());

  if (loading) {
    return <div className="py-10 text-center">Laster statistikk…</div>;
  }

  return (
    <>
      <CapacityDetailsModal
        open={modalState.open}
        onClose={closeModal}
        title={modalState.title}
        items={modalState.items}
        onOpenDetail={openDetail}
      />

      <div className="grid md:grid-cols-6 gap-4 mb-6">
        <div className="rounded border bg-gray-50 p-4">
          <div className="text-sm text-gray-600">Fortløpende kø</div>
          <div className="text-lg font-semibold">{fortlopende.count} stk</div>
          <div className="text-sm text-gray-700">{fmtMinutes(fortlopende.min)}</div>
        </div>

        <div className="rounded border bg-indigo-50 p-4">
          <div className="text-sm text-gray-600">Sykler fysisk inne</div>
          <div className="text-xl font-bold">{bikeStats.fysiskInne} stk</div>
        </div>

        <div className="rounded border bg-yellow-50 p-4">
          <div className="text-sm text-gray-600">Ikke levert enda</div>
          <div className="text-xl font-bold">{bikeStats.ikkeLevert} stk</div>
        </div>

        <div className="rounded border p-4 bg-blue-50">
          <div className="text-sm text-gray-600">Total ukekapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.kapasitet)}</div>
        </div>

        <div className="rounded border p-4 bg-amber-50">
          <div className="text-sm text-gray-600">Brukt ukekapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.brukt)}</div>
        </div>

        <div className="rounded border p-4 bg-green-50">
          <div className="text-sm text-gray-600">Ledig ukekapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.ledig)}</div>
        </div>
      </div>

      <div className="rounded border bg-white mb-8 overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50 font-semibold">
          Ukeoversikt – {prettifyStoreName(storeSlug)}
        </div>

        <table className="min-w-full">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Verksted</th>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-left">Uke start</th>
              <th className="px-4 py-2 text-left">Innlevering</th>
              <th className="px-4 py-2 text-left">Henting</th>
              <th className="px-4 py-2 text-right">Kapasitet</th>
              <th className="px-4 py-2 text-right">Brukt</th>
              <th className="px-4 py-2 text-right">Ledig</th>
              <th className="px-4 py-2 text-right">Fyllingsgrad</th>
            </tr>
          </thead>
          <tbody>
            {capacityRows.map((row) => (
              <tr
                key={row.weekIso}
                className="border-t cursor-pointer hover:bg-blue-50"
                onClick={() => openWeekModal(row)}
                title="Klikk for å se artikler/sykler i denne uken"
              >
                <td className="px-4 py-2">{prettifyStoreName(storeSlug)}</td>
                <td className="px-4 py-2 font-medium">
                  Uke {row.weekNo} / {row.year}
                </td>
                <td className="px-4 py-2">{row.weekIso}</td>
                <td className="px-4 py-2">
                  {row.isMondayClosed ? (
                    <span className="text-amber-700 font-medium">{row.intakeDate} ⚠️</span>
                  ) : (
                    row.intakeDate
                  )}
                </td>
                <td className="px-4 py-2">
                  {row.isFridayClosed ? (
                    <span className="text-amber-700 font-medium">{row.pickupDate} ⚠️</span>
                  ) : (
                    row.pickupDate
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {fmtMinutes(row.ukekapasitetMin)}
                  {row.harOverride && (
                    <span className="ml-2 text-xs text-blue-600">
                      {row.stengt ? "(stengt)" : "(override)"}
                    </span>
                  )}
                  {row.closedDayISOs?.length > 0 && (
                    <span className="ml-2 text-xs text-amber-600">
                      (-{row.closedDayISOs.length * 20}%)
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktMin)}</td>
                <td className="px-4 py-2 text-right">
                  {row.weekIso === currentWeekIso ? "–" : fmtMinutes(row.ledigMin)}
                </td>
                <td className="px-4 py-2 text-right">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      row.fyllingsgrad >= 90
                        ? "bg-red-100 text-red-700"
                        : row.fyllingsgrad >= 70
                        ? "bg-amber-100 text-amber-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {row.fyllingsgrad} %
                  </span>
                </td>
              </tr>
            ))}

            {!capacityRows.length && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                  Ingen data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <SubWeekOverviewSection
        title="Tidliguke"
        storeSlug={storeSlug}
        rows={tidligUkeRows}
        totals={tidligUkeTotals}
        currentWeekIso={currentWeekIso}
        onRowClick={openWeekModal}
      />

      <SubWeekOverviewSection
        title="Senuke"
        storeSlug={storeSlug}
        rows={senUkeRows}
        totals={senUkeTotals}
        currentWeekIso={currentWeekIso}
        onRowClick={openWeekModal}
      />

      <div className="grid md:grid-cols-5 gap-4 mb-6">
        <div className="rounded border p-4 bg-blue-50">
          <div className="text-sm text-gray-600">Total dagskapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(dayTotals.kapasitet)}</div>
        </div>

        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Brukt dag</div>
          <div className="text-xl font-bold">{fmtMinutes(dayTotals.bruktDag)}</div>
        </div>

        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Brukt drop-in</div>
          <div className="text-xl font-bold">{fmtMinutes(dayTotals.bruktDropin)}</div>
        </div>

        <div className="rounded border p-4 bg-amber-50">
          <div className="text-sm text-gray-600">Brukt totalt dag</div>
          <div className="text-xl font-bold">{fmtMinutes(dayTotals.bruktTotalt)}</div>
        </div>

        <div className="rounded border p-4 bg-green-50">
          <div className="text-sm text-gray-600">Ledig dagskapasitet</div>
          <div className="text-xl font-bold">{fmtMinutes(dayTotals.ledig)}</div>
        </div>
      </div>

      <div className="rounded border bg-white overflow-x-auto">
        <div className="px-4 py-3 border-b bg-gray-50 font-semibold">
          Dagoversikt
        </div>

        <table className="min-w-full">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Dato</th>
              <th className="px-4 py-2 text-left">Dag</th>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-right">Dagsbudsjett</th>
              <th className="px-4 py-2 text-right">Brukt dag</th>
              <th className="px-4 py-2 text-right">Brukt drop-in</th>
              <th className="px-4 py-2 text-right">Brukt totalt</th>
              <th className="px-4 py-2 text-right">Ledig</th>
              <th className="px-4 py-2 text-right">Fyllingsgrad</th>
            </tr>
          </thead>
          <tbody>
            {dayRows.map((row) => (
              <tr
                key={row.dateIso}
                className="border-t cursor-pointer hover:bg-blue-50"
                onClick={() => openDayModal(row)}
                title="Klikk for å se artikler/sykler denne dagen"
              >
                <td className="px-4 py-2">{row.dateIso}</td>
                <td className="px-4 py-2 capitalize">{row.weekday}</td>
                <td className="px-4 py-2">Uke {row.weekNo}</td>
                <td className="px-4 py-2 text-right">
                  {fmtMinutes(row.dagskapasitetMin)}
                  {row.harOverride && (
                    <span className="ml-2 text-xs text-blue-600">
                      {row.stengt ? "(stengt)" : "(override)"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktDagMin)}</td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktDropinMin)}</td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktTotaltMin)}</td>
                <td className="px-4 py-2 text-right">{fmtMinutes(row.ledigMin)}</td>
                <td className="px-4 py-2 text-right">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      row.fyllingsgrad >= 90
                        ? "bg-red-100 text-red-700"
                        : row.fyllingsgrad >= 70
                        ? "bg-amber-100 text-amber-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {row.fyllingsgrad} %
                  </span>
                </td>
              </tr>
            ))}

            {!dayRows.length && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                  Ingen dagdata
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}